import context from '@truckdown/components-systems';
import { IPromotionManager, PromotionModel } from '@truckdown/promotions';
import { defineComponent, ref, Ref, computed } from "vue";
import { showMessage, isCsrOrAdmin } from '../../Utilities/utilities';
import validationError from '../../Validation/Error/Error.vue';

interface IData {
    isLoading: Ref<boolean>,
    errors: Ref<{ [key: string]: string }>,
    dailyLimit: Ref<number>,
    disableDailyLimit: Ref<boolean>
};

const getInitialData = function (props: any) {
    let dailyLimit = props.promotion.funds.dailyLimit;
    if (dailyLimit == 0) {
        dailyLimit = undefined;
    }

    return {
        isLoading: ref<boolean>(false),
        errors: ref<{ [key: string]: string }>({}),
        dailyLimit: ref<number>(props.promotion.funds.dailyLimit),
        disableDailyLimit: ref<boolean>(props.promotion.funds.dailyLimit == 0)
    } as IData;
}

const getComputed = function (data: IData) {
    return {
        displayError: computed(() => {
            let errors = data.errors.value;
            let keys = Object.keys(errors);

            let response = {} as { [key: string]: boolean };
            for (let i = 0; i < keys.length; i++) {
                if (errors[keys[i]]) {
                    response[keys[i]] = true;
                }
            }
            return response;
        })
    }
}

const getMethods = function (props: any, data: IData, ctx: any) {
    return {
        dailyLimitCancelClick: function (): void {
            ctx.emit(events.cancel, false);
        },
        dailyLimitSaveClick: async function (): Promise<void> {
            await dailyLimitSave(data, props, ctx);
        },
        validateDailyLimit: function () {
            validateDailyLimit(data);
        },
        checkDailyLimit: function (ev: Event) {
            //fires before changing the value so need a ! check
            if (!data.disableDailyLimit.value) {
                data.dailyLimit.value = 0;
            }
        }
    }
}

const validateDailyLimit = function (data: IData) {
    delete data.errors.value['limit'];

    if (data.dailyLimit.value < 0) {
        data.disableDailyLimit.value = false;
        data.errors.value['limit'] = 'Daily limit must be set to 0 or a positive number.';
    } else if (data.dailyLimit.value == 0) {
        data.disableDailyLimit.value = true;
    } else {
        data.disableDailyLimit.value = false;
    }
}

const hasErrors = function (data: IData) {
    return Object.keys(data.errors.value).length > 0;
}

const dailyLimitSave = async function (data: IData, props: any, ctx: any): Promise<void> {
    validateDailyLimit(data);
    if (!hasErrors(data)) {
        if (!data.isLoading.value) {
            data.isLoading.value = true;
            try {
                let mgr = await context.getService<IPromotionManager>('IPromotionManager');

                let dailyLimit = data.dailyLimit.value;

                var response = await mgr.setDailyLimit(props.promotion.id, dailyLimit);

                if (!response || !response.ok) {
                    showMessage("Error updating promotion daily limit.", "danger", true, true);
                } else {
                    showMessage("Promotion daily limit was successfully updated.", "success", true, true);
                    ctx.emit(events.update, response.data);
                }
            }
            catch (err) {
                console.log(err);
                showMessage("Error updating promotion daily limit.", "danger", true, true);
                ctx.emit(events.cancel);
            }
            finally {
                data.isLoading.value = false;
            }
        }
    }
}

const events = {
    update: 'update',
    cancel: 'cancel'
}

export default defineComponent({
    props: {
        promotion: {
            type: Object as () => PromotionModel,
            required: true
        }
    },
    components: {
        'error': validationError
    },
    emits: [events.update, events.cancel],
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let computed = getComputed(data);
        let methods = getMethods(props, data, ctx);

        return {
            ...data,
            ...computed,
            ...methods,
            'isCsrOrAdmin': isCsrOrAdmin
        };
    }
});